import { ReactComponent as DownloadIcon } from "assets/icons/download.svg";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import { getDropdownValue, getNoOptionsMessage } from "helpers/DropdownHelper";
import {
    Button,
    Dropdown,
    Field,
    HeaderFilter,
    NotificationPriorityTranslation,
    NotificationSendingEntityTranslation,
    NotificationSeverityTranslation,
    NotificationTypeTranslation,
    OptionType
} from "modules/common";
import { useCountiesAndSettlements } from "modules/common/hooks/useCountiesAndSettlements";
import { useEffect, useState } from "react";
import { NotificationView } from "services";
import { downloadDocument } from "services/documents";
import { useGetDeliveryStatisticsCounty, useGetDeliveryStatisticsSettlement } from "../hooks";
import { NotificationStatistics } from "./NotificationStatistics";

interface Props {
    notification: NotificationView;
    onReturn: () => void;
}

export const SentNotificationDetails = ({ notification, onReturn }: Props) => {
    // selected dropdown options
    const [selectedCounty, setSelectedCounty] = useState<OptionType<number | null> | null>();
    const [selectedSettlement, setSelectedSettlement] = useState<OptionType<
        number | null
    > | null>();

    const { countyOptions, settlementOptions, areCountiesLoading, areSettlementsLoading } =
        useCountiesAndSettlements({
            county: selectedCounty ?? undefined,
            settlement: selectedSettlement ?? undefined,
            loadCounties: true,
        });

    // query
    const { data: deliveryStatsCounty, refetch: refetchCountyStats } =
        useGetDeliveryStatisticsCounty(
            {
                notificationId: notification.id,
                attributeId: selectedCounty?.value!,
            },
            Boolean(selectedCounty?.value)
        );
    const { data: deliveryStatsSettlement, refetch: refetchSettlementStats } =
        useGetDeliveryStatisticsSettlement(
            {
                notificationId: notification.id,
                attributeId: selectedSettlement?.value!,
            },
            Boolean(selectedSettlement?.value)
        );

    useEffect(() => {
        if (selectedCounty) refetchCountyStats();
    }, [selectedCounty]);

    useEffect(() => {
        if (selectedSettlement) refetchSettlementStats();
    }, [selectedSettlement]);

    return (
        <>
            <HeaderFilter hasBackButton onBackButtonClicked={onReturn}>
                <div className="header-filter-title">PODACI O PORUCI</div>
            </HeaderFilter>

            <div className="sent-notification-details">
                <Field spanFieldColumn={3} fieldColumns={3} horizontal name="NASLOV">
                    {notification.subject}
                </Field>
                <Field spanFieldColumn={3} fieldColumns={3} horizontal name="DATUM SLANJA">
                    {getDisplayDateFormat(notification.scheduledAt as any, true)}
                </Field>
                <Field spanFieldColumn={3} fieldColumns={3} horizontal name="NAČIN SLANJA">
                    {getDropdownValue(notification.type, NotificationTypeTranslation).label}
                </Field>
                <Field spanFieldColumn={3} fieldColumns={3} horizontal name="VRSTA PORUKE">
                    {getDropdownValue(notification.severity, NotificationSeverityTranslation).label}
                </Field>
                <Field spanFieldColumn={3} fieldColumns={3} horizontal name="PRIORITET">
                    {getDropdownValue(notification.priority, NotificationPriorityTranslation).label}
                </Field>
                <Field spanFieldColumn={3} fieldColumns={3} horizontal name="STRUČNO PODRUČJE">
                    {notification.professionalField}
                </Field>
                <Field spanFieldColumn={3} fieldColumns={3} horizontal name="POŠILJATELJ">
                    {notification.sender}
                </Field>
                <Field
                    spanFieldColumn={3}
                    fieldColumns={3}
                    horizontal
                    name="AUTENTIFICIRANI POŠILJATELJ"
                >
                    {
                        getDropdownValue(
                            notification.sendingEntity,
                            NotificationSendingEntityTranslation
                        ).label
                    }
                </Field>
                <Field spanFieldColumn={3} fieldColumns={3} horizontal name="SADRŽAJ PORUKE">
                    {notification.isHtml ? (
                        <div dangerouslySetInnerHTML={{ __html: notification.body }} />
                    ) : (
                        notification.body
                    )}
                </Field>
                <Field spanFieldColumn={3} fieldColumns={3} horizontal name="PRIVITAK">
                    <div>
                        {notification.attachments.map(attachment => {
                            return (
                                <Button
                                    width="wide"
                                    variant="primary"
                                    key={attachment.id}
                                    size="small"
                                    className="attachment-name"
                                    onClick={() => downloadDocument(attachment.fileUrl)}
                                >
                                    <div>
                                        <span>{attachment.name}</span>
                                        <DownloadIcon />
                                    </div>
                                </Button>
                            );
                        })}
                    </div>
                </Field>

                <div>
                    <span className="notification-section-header">STATISTIKA O PRIMATELJIMA</span>
                    <div className="sent-notification-details-statistics">
                        <NotificationStatistics
                            existingNotificationId={notification.id}
                            spanDisplay
                        />
                        <div></div>
                    </div>
                </div>

                <span className="notification-section-header">
                    STATISTIKA ISPORUČENOSTI PORUKE / PROČITANOSTI
                </span>

                <Field horizontal fieldColumns={3} spanFieldColumn={2}>
                    <span>ISPORUČENO</span>
                    <span>PROČITANO</span>
                </Field>

                <Field
                    horizontal
                    name="UKUPNI BROJ PRIMATELJA"
                    spanFieldColumn={2}
                    fieldColumns={3}
                >
                    <span>
                        {`${notification.deliveryStats} od ${notification.total} = ${
                            notification.total !== 0
                                ? Math.round(
                                      (notification.deliveryStats / notification.total) * 100
                                  )
                                : 0
                        }%`}
                    </span>
                    <span>
                        {`${notification.readStats} od ${notification.total} = ${
                            notification.total !== 0
                                ? Math.round((notification.readStats / notification.total) * 100)
                                : 0
                        }%`}
                    </span>
                </Field>

                <Field
                    horizontal
                    name="BROJ PRIMATELJA PO ŽUPANIJI"
                    spanFieldColumn={3}
                    fieldColumns={3}
                >
                    <span>
                        {selectedCounty
                            ? deliveryStatsCounty
                                ? `${deliveryStatsCounty.countDelivered} od ${
                                      deliveryStatsCounty.total
                                  } = ${
                                      deliveryStatsCounty.total !== 0
                                          ? Math.round(
                                                (deliveryStatsCounty?.countDelivered! /
                                                    deliveryStatsCounty?.total!) *
                                                    100
                                            )
                                          : 0
                                  }%`
                                : "0 od 0 = 0%"
                            : "/"}
                    </span>
                    <span>
                        {selectedCounty
                            ? deliveryStatsCounty
                                ? `${deliveryStatsCounty.countRead} od ${
                                      deliveryStatsCounty.total
                                  } = ${
                                      deliveryStatsCounty.total !== 0
                                          ? Math.round(
                                                (deliveryStatsCounty?.countRead! /
                                                    deliveryStatsCounty?.total!) *
                                                    100
                                            )
                                          : 0
                                  }%`
                                : "0 od 0 = 0%"
                            : "/"}
                    </span>
                    <Dropdown
                        maxMenuHeight={200}
                        size="medium"
                        placeholder="Odaberite županiju"
                        value={selectedCounty}
                        onChange={e => {
                            setSelectedSettlement(null);
                            setSelectedCounty(e!);
                        }}
                        options={countyOptions}
                    />
                </Field>

                <Field
                    horizontal
                    name="BROJ PRIMATELJA PO MJESTU"
                    spanFieldColumn={3}
                    fieldColumns={3}
                >
                    <span>
                        {selectedSettlement
                            ? deliveryStatsSettlement
                                ? `${deliveryStatsSettlement.countDelivered} od ${
                                      deliveryStatsSettlement.total
                                  } = ${
                                      deliveryStatsSettlement.total !== 0
                                          ? Math.round(
                                                (deliveryStatsSettlement?.countDelivered! /
                                                    deliveryStatsSettlement?.total!) *
                                                    100
                                            )
                                          : 0
                                  }%`
                                : "0 od 0 = 0%"
                            : "/"}
                    </span>
                    <span>
                        {selectedSettlement
                            ? deliveryStatsSettlement
                                ? `${deliveryStatsSettlement.countRead} od ${
                                      deliveryStatsSettlement.total
                                  } = ${
                                      deliveryStatsSettlement.total !== 0
                                          ? Math.round(
                                                (deliveryStatsSettlement?.countRead! /
                                                    deliveryStatsSettlement?.total!) *
                                                    100
                                            )
                                          : 0
                                  }%`
                                : "0 od 0 = 0%"
                            : "/"}
                    </span>
                    <Dropdown
                        noOptionsMessage={() =>
                            getNoOptionsMessage(
                                areCountiesLoading,
                                !Boolean(selectedCounty),
                                "Odaberite županiju"
                            )
                        }
                        value={selectedSettlement}
                        placeholder="Odaberite mjesto"
                        options={settlementOptions}
                        isLoading={areSettlementsLoading}
                        onChange={e => setSelectedSettlement(e!)}
                        maxMenuHeight={200}
                        size="medium"
                    />
                </Field>
            </div>
        </>
    );
};
